import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';

/** Renders a single article section with subsections, bullets, images, callouts */
export const ArticleSection = ({ section, index }) => {
  const isEven = index % 2 === 0;

  return (
    <section id={section.id} className="scroll-mt-24" data-testid={`article-section-${section.id}`}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.4 }}
      >
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 mb-4">
          {section.title}
        </h2>

        {section.content && (
          <div className="text-stone-700 leading-relaxed mb-6 space-y-4">
            {section.content.split('\n\n').map((p, i) => (
              <p key={`p-${i}-${p.slice(0, 20)}`}>{p}</p>
            ))}
          </div>
        )}

        {/* Callout block */}
        {section.callout && (
          <div className="bg-primary/5 border-l-4 border-primary rounded-r-lg px-6 py-5 mb-8">
            <p className="text-base font-medium text-stone-800 italic leading-relaxed">
              "{section.callout.text}"
            </p>
          </div>
        )}

        {/* Section-level bullets */}
        {section.bullets && (
          <ul className="space-y-2.5 mb-6 pl-1">
            {section.bullets.map((b, i) => (
              <li key={`bullet-${i}-${b.slice(0, 20)}`} className="flex items-start gap-3 text-stone-700">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                <span className="leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        )}

        {section.afterContent && (
          <p className="text-stone-700 leading-relaxed mb-6">{section.afterContent}</p>
        )}

        {/* Section image */}
        {section.image && (
          <figure className="my-8 rounded-xl overflow-hidden shadow-md">
            <img
              src={section.image}
              alt={section.imageAlt || section.title}
              className="w-full h-auto object-cover max-h-[400px]"
              loading="lazy"
            />
            {section.imageCaption && (
              <figcaption className="bg-stone-50 px-4 py-2 text-xs text-stone-500 italic">
                {section.imageCaption}
              </figcaption>
            )}
          </figure>
        )}

        {/* Subsections */}
        {section.subsections && (
          <div className="space-y-8 mt-6">
            {section.subsections.map((sub, si) => (
              <div key={sub.title || `sub-${si}`} className="pl-0 md:pl-4 border-l-0 md:border-l-2 border-stone-100">
                <h3 className="font-heading text-lg md:text-xl font-semibold text-stone-800 mb-3 md:pl-4">
                  {sub.title}
                </h3>
                {sub.content && (
                  <div className="text-stone-600 leading-relaxed mb-3 md:pl-4 space-y-3">
                    {sub.content.split('\n\n').map((p, i) => (
                      <p key={`sub-p-${i}-${p.slice(0, 20)}`}>{p}</p>
                    ))}
                  </div>
                )}
                {sub.bullets && (
                  <ul className="space-y-2 mb-3 md:pl-4">
                    {sub.bullets.map((b, i) => (
                      <li key={`sub-b-${i}-${b.slice(0, 20)}`} className="flex items-start gap-3 text-stone-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/60 mt-2.5 flex-shrink-0" />
                        <span className="leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {sub.afterBullets && (
                  <div className="text-stone-600 leading-relaxed md:pl-4 space-y-3">
                    {sub.afterBullets.split('\n\n').map((p, i) => (
                      <p key={`sub-ab-${i}-${p.slice(0, 20)}`}>{p}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </section>
  );
};
